'use client';

import {
  createContext,
  useContext,
  type ReactNode,
  useCallback,
  useRef,
  useEffect,
  useState,
} from 'react';
import { useScrollStore } from '@/store/scrollStore';
import { usePathname } from 'next/navigation';
import { logger } from '@/config';

interface ScrollContextType {
  saveScrollPosition: () => void;
  restoreScrollPosition: () => void;
  savePageIndex: (index: number) => void;
  getPageIndex: () => number;
  clearScrollPosition: () => void;
  isScrollRestored: boolean;
  setScrollRestored: (value: boolean) => void;
  saveScrollData: (scrollY: number, pageIndex: number) => void;
  getScrollData: () => { position: number; pageIndex: number };
  // New methods for better PLP integration
  saveScrollPositionImmediate: (scrollY: number) => void;
  shouldRestoreScroll: () => boolean;
  isFromPdp: boolean;
  setIsFromPdp: (value: boolean) => void;
}

// Create the context with a default value
const ScrollContext = createContext<ScrollContextType>({
  saveScrollPosition: () => {},
  restoreScrollPosition: () => {},
  savePageIndex: () => {},
  getPageIndex: () => 1,
  clearScrollPosition: () => {},
  isScrollRestored: false,
  setScrollRestored: () => {},
  saveScrollData: () => {},
  getScrollData: () => ({ position: 0, pageIndex: 1 }),
  saveScrollPositionImmediate: () => {},
  shouldRestoreScroll: () => false,
  isFromPdp: false,
  setIsFromPdp: () => {},
});

export const ScrollProvider = ({ children }: { children: ReactNode }) => {
  const [mounted, setMounted] = useState(false);
  const [isFromPdp, setIsFromPdp] = useState(false);
  const pathname = usePathname();
  const scrollTimeoutRef = useRef<NodeJS.Timeout>(null);
  const {
    setPlpScrollPosition,
    getPlpScrollPosition,
    setPlpPageIndex,
    getPlpPageIndex,
    clearPlpScrollPosition,
    isScrollRestored,
    setScrollRestored,
    hasScrollData,
  } = useScrollStore();

  // Ensure we're mounted before providing context
  useEffect(() => {
    setMounted(true);
    return () => setMounted(false);
  }, []);

  const saveScrollPosition = useCallback(() => {
    if (typeof window === 'undefined' || !mounted) return;

    if (scrollTimeoutRef.current) {
      clearTimeout(scrollTimeoutRef.current);
    }

    scrollTimeoutRef.current = setTimeout(() => {
      setPlpScrollPosition(pathname, window.scrollY);
    }, 100);
  }, [pathname, setPlpScrollPosition, mounted]);

  const saveScrollPositionImmediate = useCallback(
    (scrollY: number) => {
      if (!mounted) return;
      setPlpScrollPosition(pathname, scrollY);
    },
    [pathname, setPlpScrollPosition, mounted]
  );

  const restoreScrollPosition = useCallback(() => {
    if (typeof window === 'undefined' || !mounted || isScrollRestored) return;

    const position = getPlpScrollPosition(pathname);
    if (position > 0) {
      requestAnimationFrame(() => {
        window.scrollTo(0, position);
        setScrollRestored(true);
      });
    } else {
      setScrollRestored(true);
    }
  }, [
    pathname,
    getPlpScrollPosition,
    isScrollRestored,
    setScrollRestored,
    mounted,
  ]);

  const savePageIndex = useCallback(
    (index: number) => {
      if (!mounted) return;
      setPlpPageIndex(pathname, index);
    },
    [pathname, setPlpPageIndex, mounted]
  );

  const getPageIndex = useCallback(() => {
    if (!mounted) return 1;
    return getPlpPageIndex(pathname);
  }, [pathname, getPlpPageIndex, mounted]);

  const clearScrollPosition = useCallback(() => {
    if (!mounted) return;
    clearPlpScrollPosition(pathname);
    setScrollRestored(false);
  }, [pathname, clearPlpScrollPosition, setScrollRestored, mounted]);

  const shouldRestoreScroll = useCallback(() => {
    if (!mounted) return false;
    return hasScrollData(pathname);
  }, [pathname, hasScrollData, mounted]);

  const saveScrollData = useCallback(
    (scrollY: number, pageIndex: number) => {
      if (!mounted) return;
      setPlpScrollPosition(pathname, scrollY);
      setPlpPageIndex(pathname, pageIndex);
    },
    [pathname, setPlpScrollPosition, setPlpPageIndex, mounted]
  );

  const getScrollData = useCallback(() => {
    if (!mounted) return { position: 0, pageIndex: 1 };
    return {
      position: getPlpScrollPosition(pathname),
      pageIndex: getPlpPageIndex(pathname),
    };
  }, [pathname, getPlpScrollPosition, getPlpPageIndex, mounted]);

  useEffect(() => {
    return () => {
      if (scrollTimeoutRef.current) {
        clearTimeout(scrollTimeoutRef.current);
      }
    };
  }, []);

  const contextValue = {
    saveScrollPosition,
    restoreScrollPosition,
    savePageIndex,
    getPageIndex,
    clearScrollPosition,
    isScrollRestored,
    setScrollRestored,
    saveScrollData,
    getScrollData,
    saveScrollPositionImmediate,
    shouldRestoreScroll,
    isFromPdp,
    setIsFromPdp,
  };

  return (
    <ScrollContext.Provider value={contextValue}>
      {children}
    </ScrollContext.Provider>
  );
};

export const useScroll = () => {
  const context = useContext(ScrollContext);
  if (!context) {
    logger.error('useScroll must be used within a ScrollProvider');
    // Return default values instead of throwing to prevent app crashes
    return {
      saveScrollPosition: () => {},
      restoreScrollPosition: () => {},
      savePageIndex: () => {},
      getPageIndex: () => 1,
      clearScrollPosition: () => {},
      isScrollRestored: false,
      setScrollRestored: () => {},
      saveScrollData: () => {},
      getScrollData: () => ({ position: 0, pageIndex: 1 }),
      saveScrollPositionImmediate: () => {},
      shouldRestoreScroll: () => false,
      isFromPdp: false,
      setIsFromPdp: () => {},
    };
  }
  return context;
};
